import { ProductViewSchema } from '@fsf/types';
import { _ } from '@ngx-translate/core';
import { z } from 'zod';
import {
  DEFAULT_LANGUAGE_CODE,
  LanguageCodeSchema,
} from '../../util/language-codes';

export type Product = z.infer<typeof ProductViewSchema>;

const detailSchema = z.object({
  language: LanguageCodeSchema,
  title: z.string().min(1, { message: 'validation.required' }).max(255),
  description_short: z.string().nullable().optional(),
  description_long: z.string().nullable().optional(),
});

export const StoreProductSchema = z.object({
  category_id: z.number().int().min(1, { message: 'validation.required' }),
  opening_hour_type_id: z
    .number()
    .int()
    .min(1, { message: 'validation.required' }),
  key: z.string().min(1, { message: 'validation.required' }).max(255),
  duration: z.number().int().min(0).nullable().optional(),
  interval: z
    .number()
    .int()
    .min(30)
    .refine((v) => v % 30 === 0, { message: 'validation.intervalMustBeMultipleOf30' })
    .nullable()
    .optional(),
  for_request: z.boolean().nullable().optional(),
  active: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  additional_product_ids: z.array(z.number().int()).optional(),
  details: z
    .array(detailSchema)
    .min(1)
    .superRefine((rows, ctx) => {
      const langs = rows.map((r) => r.language);
      if (new Set(langs).size !== langs.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: _('validation.duplicateLanguage'),
        });
      }
      if (!langs.includes(DEFAULT_LANGUAGE_CODE)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: _('validation.defaultLanguageRequired'),
        });
      }
    }),
});

export type StoreProductPayload = z.infer<typeof StoreProductSchema>;
export const UpdateProductSchema = StoreProductSchema.partial();
export type UpdateProductPayload = z.infer<typeof UpdateProductSchema>;

export const productTranslations = _([
  'catalog.products.title',
  'catalog.products.add',
  'catalog.products.edit',
  'catalog.products.empty',
  'catalog.products.deleteConfirmHeader',
  'catalog.products.deleteConfirmMessage',
  'catalog.products.created',
  'catalog.products.updated',
  'catalog.products.deleted',
  'catalog.products.notFound',
  'catalog.products.loadError',
  'catalog.products.createError',
  'catalog.products.updateError',
  'catalog.products.deleteError',
  'catalog.products.dashboardDescription',
  'catalog.products.dashboardButton',
  'catalog.products.fields.titleDe',
  'catalog.products.fields.titleEn',
  'catalog.products.fields.descriptionShortDe',
  'catalog.products.fields.descriptionShortEn',
  'catalog.products.fields.descriptionLongDe',
  'catalog.products.fields.descriptionLongEn',
  'catalog.products.fields.key',
  'catalog.products.fields.category',
  'catalog.products.fields.openingHourType',
  'catalog.products.fields.duration',
  'catalog.products.fields.interval',
  'catalog.products.fields.forRequest',
  'catalog.products.fields.active',
  'catalog.products.fields.notes',
  'catalog.products.bundleRuleHint',
  'catalog.products.displayPrice',
]);
